import { SalesPromoFormProps } from "./SalesPromoForm.types";
import { HookFormProvider, InputSelect } from 'src/app/components/HookForm';
import { Alert, Button, ControlLabel, DialogRef, Stack, Typography } from 'src/app/components';
import { usePermissions } from 'src/app/hooks/permissions-hook';
import { useRef } from 'react';
import SelectAllCheckbox from 'src/app/components/Inputs/SelectAllCheckbox/SelectAllCheckbox.page';
import { DeleteIcon } from 'src/app/components/Icons';
import { SalesPromoModel, SalesPromoRuleModel, PrizeRuleDetail, TransactionRule } from '../../../models/SalesPromo.model';
import { Refs } from 'src/app/utils/refs-utils';
import { getErrorMessage } from 'src/app/utils/api-utils';
import { SalesPromoTransaction } from "./SalesPromoForm3Child/SalesPromoTransaction.page";
import { useSalesPromoForm3 } from "./SalesPromoForm3.hook";

export function SalesPromoForm3(props: Readonly<SalesPromoFormProps>) {
    const alertSaveAsDraftRef = useRef<DialogRef>(null);
    const state = useSalesPromoForm3(props.currentDetail)
    const permission = usePermissions()
    const is_direct_cashback_point = props.currentDetail?.is_direct_cashback_point ?? false
    const is_mission_collect_stamp = props.currentDetail?.is_mission_collect_stamp ?? false
    const isEdit = !!props.id && (props.currentDetail?.status === 'PENDING' || props.currentDetail?.status === 'ACTIVE')
    const saveAndExit = () => {
        const { status } = props.currentDetail!;
        const isPendingOrActive = status === 'PENDING' || status === 'ACTIVE';
        props.doPreventExit?.(false);
        isPendingOrActive ? saveWithTrigger() : saveAsDraft('STAY');
    };

    const saveWithTrigger = state.form.handleSubmit(() => saveAsDraft('STAY'));

    const transformSalesPromoData = (formData: any, isDraft = false) => ({
        prize_rules: formData.prize_rules.map(({ merchant_categories, merchants, sales_promo_rules, ...store }: SalesPromoRuleModel) => ({
            ...store,
            is_draft: isDraft,
            merchant_ids: merchants?.map(m => m?.id) ?? [],
            merchants: merchants?.map(m => ({
                id: m.id,
                unique_merchant_name: m.unique_merchant_name
            })) ?? [],
            merchant_category_ids: merchant_categories?.map(c => c?.id) ?? [],
            sales_promo_rules: (sales_promo_rules ?? []).map(({ payment_categories, payment_methods, minimum_transaction, prizes, is_mixable, is_multiple_entry, ...rule }: TransactionRule) => ({
                ...rule,
                is_draft: isDraft,
                minimum_transaction: minimum_transaction ? +minimum_transaction : 0,
                is_mixable: is_mixable ?? false,
                is_multiple_entry: is_multiple_entry ?? false,
                ...(is_direct_cashback_point ? {} : {
                    prizes: (prizes ?? []).map(({ prizes, ...prize }: PrizeRuleDetail) => ({
                        ...prize,
                        is_draft: isDraft,
                    }))
                })
            })),
        })),
    });

    const saveAsDraft = async (key: string) => {
        alertSaveAsDraftRef.current?.hide();
        const formData = state.form.getValues();
        const updatedSalesPromoData = transformSalesPromoData(formData, true);
        hitPostAPI(updatedSalesPromoData.prize_rules, key)
    };

    const submitButtonClick = state.form.handleSubmit(async (formData) => {
        const updatedSalesPromoData = transformSalesPromoData(formData);
        hitPostAPI(updatedSalesPromoData.prize_rules, 'SUBMIT');
    });

    const hitPostDraft = async () => {
        const payload: SalesPromoModel = {
            id: props.id,
            is_draft: true,
        }
        await state.saveSalesPromoGeneral.mutateAsync(payload)
    }

    const hitPostAPI = async (payload: SalesPromoRuleModel[], key: string) => {
        try {
            await state.saveSalesPromoRule.mutateAsync(payload)
            if (key === 'SUBMIT') {
                props.save && props.save()
            } else {
                const { status } = props.currentDetail!;
                const isActive = status === 'ACTIVE';
                if (!isActive) { await hitPostDraft() } else {
                    Refs.pageToast.show({
                        severity: 'success',
                        message: "Sales Promo successfully edited."
                    });
                }
                props.saveDraft && props.saveDraft(key)
                state.rules.refetch()
            }
        } catch (error: any) {
            if (error?.response?.data?.response_schema?.response_code === 'PAKUWON-400') {
                const axiosResponse = error?.response ?? undefined
                if (axiosResponse) {
                    axiosResponse?.data?.response_output?.errors?.forEach((it: any) => {
                        if (it.field) {
                            const field: any = convertFieldName(it.field)
                            state.form.setError(field, { type: 'custom', message: it.message.en })
                        }
                    })
                }
                window.scrollTo({
                    top: 350,
                    behavior: 'smooth',
                })
                Refs.pageToast.show({
                    severity: 'danger',
                    message: `${getErrorMessage(error, true)?.en}`
                });
            }
        }
    }

    function convertFieldName(field: string): string {
        return field.replace(/merchant_ids/g, 'merchants');
    }

    const addNewStoreClicked = state.form.handleSubmit(async () => {
        state.addNewStore()
    })

    const getDisabled = (index: number) => {
        const prizeRulesLength = state.prizeRuleLength?.prize_rules_length ?? 0
        return prizeRulesLength > index
    }

    return (
        <>
            <HookFormProvider form={state.form} >
                <Stack className="p-6">
                    <p className="text-xs text-gray-400 dark:text-gray-500">{props.currentDetail?.name || '-'}</p>
                    {state.storeFields.map((field, storeIndex) => (
                        <Stack key={field.id}>
                            <div className='flex items-center justify-between'>
                                <Typography variant='h5'>Store {storeIndex + 1}</Typography>
                                {storeIndex > 0 &&
                                    <Button startIcon={DeleteIcon} variant='text' color='danger'
                                        disabled={getDisabled(storeIndex)}
                                        onClick={() => state.removeStore(storeIndex)}>
                                        Delete
                                    </Button>
                                }
                            </div>
                            <SelectAllCheckbox
                                label="Select All Store Category"
                                onChecked={(_) => {
                                    state.handleSyncCheckedAllStore(true, storeIndex)
                                }}
                                onUnChecked={() => {
                                    state.handleSyncCheckedAllStore(false, storeIndex)
                                }}
                                checked={
                                    (state.form.watch(`prize_rules.${storeIndex}.merchant_categories`)?.length === state.store_categories.data?.content?.length) &&
                                    (state.form.watch(`prize_rules.${storeIndex}.merchants`)?.length === state.store.data?.content?.length)
                                }
                                disabled={getDisabled(storeIndex)}
                                selectOption={(_) => ({
                                    label: _._!,
                                    value: _._!,
                                })}
                            />
                            <ControlLabel label="Store Category">
                                <InputSelect
                                    onChangeValue={(data) => state.handleSyncStoreCategory(data, storeIndex)}
                                    placeholder="Select store category"
                                    isMulti={true}
                                    options={state.store_categories.data?.content}
                                    rule={{ required: true }}
                                    form={state.form}
                                    name={`prize_rules.${storeIndex}.merchant_categories`}
                                    isDisabled={getDisabled(storeIndex)}
                                    selectOption={(_) => ({
                                        label: _.category_name!,
                                        value: _.id!,
                                    })}
                                />
                            </ControlLabel>
                            <ControlLabel label="Store">
                                <InputSelect
                                    onChangeValue={(data) => state.handleSyncStore(data, storeIndex)}
                                    placeholder="Select store"
                                    isMulti={true}
                                    options={state.store.data?.content}
                                    rule={{ required: true }}
                                    form={state.form}
                                    name={`prize_rules.${storeIndex}.merchants`}
                                    // isDisabled={getDisabled(storeIndex)}
                                    selectOption={(_) => ({
                                        label: _.unique_merchant_name!,
                                        value: _.id!,
                                    })}
                                />
                            </ControlLabel>
                            <SalesPromoTransaction id={props.id} storeIndex={storeIndex} prizeRuleLength={state.prizeRuleLength} is_direct_cashback_point={is_direct_cashback_point} is_mission_collect_stamp={is_mission_collect_stamp} state={state} campaignStatus={props.currentDetail?.status} />
                            <ControlLabel label=''>
                                <Button variant='outlined' onClick={addNewStoreClicked}>Add New Store</Button>
                            </ControlLabel>
                        </Stack>
                    ))}
                </Stack>
                <Stack
                    className={'px-8 py-6 border-t border-neutral-200'}
                    direction="row"
                    gap={4}
                >
                    {permission.setVisibleByAccess(
                        ['manage_campaign_sales_promo.create', 'manage_campaign_sales_promo.update'],
                        <Button variant='outlined' loading={state.saveSalesPromoGeneral.isLoading} onClick={() => { isEdit ? saveAndExit() : alertSaveAsDraftRef.current?.show() }}>{'Save' + (!isEdit ? ' as Draft' : '')}</Button>,
                    )}

                    <Stack
                        direction="row"
                        justify='end'
                        gap={4}
                    >
                        <Button variant='outlined' loading={state.saveSalesPromoRule.isLoading} onClick={() => { props.backward && props.backward() }}>Previous</Button>
                        {permission.setVisibleByAccess(
                            ['manage_campaign_sales_promo.create', 'manage_campaign_sales_promo.update'],
                            <Button loading={state.saveSalesPromoRule.isLoading} onClick={() => submitButtonClick()}>Next</Button>,
                        )}

                    </Stack>
                </Stack>
            </HookFormProvider>

            <Alert
                cancelText="Go to List"
                submitText="Stay on page"
                onCancel={() => saveAsDraft('BACK')}
                onSubmit={() => saveAsDraft('STAY')}
                title="Save as Draft?"
                ref={alertSaveAsDraftRef}
                closeOnBackdropClicked={false}
            >
                <Typography>
                    Your progress will be saved as a draft. You can continue editing later.
                    <br />Would you like to stay on this page or return to the list?
                </Typography>
            </Alert>
        </>
    )
}
